import { DrillPageGlyphBlock } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.glyph-block";
import {
  DrillPageGlyphPrompt,
  useDrillPageGlyphPromptGate,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.glyph-prompt";
import {
  DrillPageHeaderTexts,
  type DrillPageHeaderTextsVariant,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.header-texts";
import {
  DrillPagePronunciationHint,
  useDrillPagePronunciationHintGate,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.pronunciation-hint";
import { DrillPageWordSentencePrompt } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.word-sentence-prompt";
import { DrillPageSentencePrompt } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.sentence-prompt";
import {
  DrillPageContext,
  GraphemeDrillContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { DrillPageContainer } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.container";
import { DrillPageGlyphStyleToggle } from "@-/dev.improve.haniverse.routes.nav.app.learn._components.glyph-style-toggle";
import { useDrillPageKeyboardAwareContentStyle } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.keyboard-aware-content-style";
import { useDrillPageLandscapeLayout } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.landscape-layout";
import { View } from "@-/com.npmjs.react-native";
import Animated from "@-/com.npmjs.react-native-reanimated";

function PronunciationLandscapeLayout({ pinyin }: { pinyin: string }) {
  const { isLandscapeCompressed } = useDrillPageLandscapeLayout();
  const pronunciationHintGate = useDrillPagePronunciationHintGate(pinyin);
  return (
    <DrillPageContainer
      headerCenter={<DrillPageHeaderTexts section="heading" variant="landscape" />}
      headerEnd={<DrillPageGlyphStyleToggle position="card-header" />}
    >
      <View className="min-h-0 flex-1 flex-col px-2.25 pb-2.25" pointerEvents="none">
        {pronunciationHintGate === null ? (
          <>
            <View className="shrink-0">
              <DrillPageHeaderTexts section="prompt" variant="landscape" />
            </View>
            <DrillPageGlyphBlock />
          </>
        ) : (
          <>
            <View className="min-h-0 flex-1 items-center justify-center px-3">
              <DrillPageHeaderTexts section="prompt" variant="landscape" />
              <DrillPagePronunciationHint
                gate={pronunciationHintGate}
                variant={isLandscapeCompressed ? "landscape-compact" : "landscape"}
              />
            </View>
            <View className="h-16 shrink-0 items-center pt-2">
              <DrillPageGlyphBlock />
            </View>
          </>
        )}
      </View>
    </DrillPageContainer>
  );
}

function MeaningLandscapeLayout() {
  return (
    <DrillPageContainer
      headerCenter={<DrillPageHeaderTexts section="heading" variant="landscape" />}
      headerEnd={<DrillPageGlyphStyleToggle position="card-header" />}
    >
      <View className="min-h-0 flex-1 flex-col px-2.25 pb-2.25" pointerEvents="none">
        <View className="shrink-0">
          <DrillPageHeaderTexts section="prompt" variant="landscape" />
        </View>
        <View className="min-h-0 flex-1">
          <DrillPageGlyphBlock />
        </View>
      </View>
    </DrillPageContainer>
  );
}

/** A grapheme drill's landscape front. */
export function GraphemeDrillPageLandscapeView() {
  const [drillType] = useContextSelectors(GraphemeDrillContext, (c) => c.drillType);
  return drillType.kind === "pronunciation" ? (
    <PronunciationLandscapeLayout pinyin={drillType.pinyin} />
  ) : (
    <MeaningLandscapeLayout />
  );
}

/** A word drill's landscape front: the sentence prompt, or the sentence being produced. */
export function WordDrillPageLandscapeView() {
  const { keyboardAwareContentStyle } = useDrillPageKeyboardAwareContentStyle();
  const { handleLandscapeMainContentLayout, isLandscapeCompressed } = useDrillPageLandscapeLayout();
  const layoutVariant: DrillPageHeaderTextsVariant = isLandscapeCompressed
    ? "landscape-compact"
    : "landscape";
  const [sentenceDrill] = useContextSelectors(DrillPageContext, (c) => c.sentenceDrill);
  const glyphPromptGate = useDrillPageGlyphPromptGate();

  return (
    <DrillPageContainer>
      <View
        className="min-h-0 flex-1"
        onLayout={handleLandscapeMainContentLayout}
        pointerEvents="box-none"
      >
        <DrillPageGlyphStyleToggle />

        <Animated.View
          className="min-h-0 flex-1 flex-row"
          pointerEvents="box-none"
          style={keyboardAwareContentStyle}
        >
          {/* A word card's glyph IS the answer, so the left column carries the
              translation + pinyin prompt (or the sentence being produced) instead of a glyph. */}
          <View className="min-h-0 flex-1 flex-col px-3" pointerEvents="none">
            <View className="min-h-0 flex-1 items-center justify-center">
              {sentenceDrill === null ? (
                <DrillPageWordSentencePrompt variant={layoutVariant} />
              ) : (
                <DrillPageSentencePrompt sentenceDrill={sentenceDrill} variant={layoutVariant} />
              )}
            </View>
            {!isLandscapeCompressed && glyphPromptGate !== null && (
              <DrillPageGlyphPrompt gate={glyphPromptGate} variant="landscape" />
            )}
          </View>
          <View
            className={
              isLandscapeCompressed
                ? "min-h-0 flex-1 items-center justify-center gap-0.5 px-3"
                : "min-h-0 flex-1 items-center justify-center gap-1 px-6"
            }
            pointerEvents="none"
          >
            <DrillPageHeaderTexts variant={layoutVariant} />
          </View>
        </Animated.View>
      </View>
    </DrillPageContainer>
  );
}
